import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { FormSpy } from 'react-final-form';
import * as R from 'ramda';

import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { getResultDecisionInspectorBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import {
  getKnmDataCreated,
  getKnmErknmParticipantPositionsSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import styles from './ErknmResultDecisionsItem.module.scss';
import { IKnmErknmFormAct } from '../../MainModule/interfaces/IKnmErknmFormAct';
import { EditButton } from '../../../UIPackage/MainModule/components/EditButton';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

interface IErknmResultDecisionInspectorsProps {
  acts: IKnmErknmFormAct[] | undefined;
  addField: (fieldName: string) => () => void;
  addNewField: (fieldName: string) => () => void;
  decisionIndex: number;
  id: string;
  lateFieldName: string;
  makeRemoveField: (fieldName: string, index: number) => () => void;
  name: string;
  value: IKnmErknmFormResultDecision;
  watchOnly: boolean;
}

interface IErknmResultDecisionInspectorsStoreProps {
  created: string | null | undefined;
  participantPositionsSelectOptions: IDictionaryParticipantPositionSelectOption[] | undefined;
  settings: IAdminSetting[] | undefined;
}

const ErknmResultDecisionInspectorsWithoutStore = ({
  acts,
  addField,
  addNewField,
  decisionIndex,
  id,
  lateFieldName,
  makeRemoveField,
  name,
  value,
  watchOnly,
  created,
  participantPositionsSelectOptions,
  settings,
}: IErknmResultDecisionInspectorsProps & IErknmResultDecisionInspectorsStoreProps) => {
  const inspectorsBlockId = `${id}.inspectors`;

  const [inspectorsSelectorOrInput, setInspectorsSelectorOrInput] = useState<boolean[]>([]);

  /**
   * Список вариантов для выбора должностного лица
   */
  const inspectorsActsOptions = useMemo(() => {
    const options: {
      id: string;
      value: IKnmErknmFormInspector;
      label: string;
    }[] = [];

    acts?.forEach(act => {
      act.knoInspectors?.forEach(item => {
        if (item.knoInspector?.value && !options.find(i => i.id === item.knoInspector?.value.guid)) {
          options.push({
            id: item.knoInspector.value.guid || '',
            value: item.knoInspector.value,
            label: `${item.knoInspector.value.fullName} - ${item.knoInspector.value.position?.label}`,
          });
        }
      });
    });

    return options;
  }, [acts]);

  useEffect(() => {
    if (value.inspectors?.length) {
      setInspectorsSelectorOrInput(
        value.inspectors.map(inspector => {
          if (inspector?.fullName !== undefined) {
            return !!inspectorsActsOptions.find(i => i.label === `${inspector.fullName} - ${i.value.position?.title}`);
          }

          return true;
        }),
      );
    }
  }, [value, inspectorsActsOptions]);

  /**
   * Функция изменения типа ввода инспектора участвовавшего в проверке
   */
  const onChangeInspectorTypeInput = useCallback(
    (fields, index) => {
      const newValue = [...inspectorsSelectorOrInput];

      newValue[index] = !inspectorsSelectorOrInput[index];
      setInspectorsSelectorOrInput(newValue);

      const isNew = fields.value?.[index]?.isNew;

      if (newValue[index]) {
        fields.update(index);
      } else {
        fields.update(index, { guid: uuidv4(), isNew, fullName: '' });
      }
    },
    [inspectorsSelectorOrInput],
  );

  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );

  return (
    <FieldBlock
      label={
        <>Должностные лица контрольного (надзорного) органа, участвовавшие в контрольных (надзорных) мероприятиях</>
      }
      id={inspectorsBlockId}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly}
          onClick={isShow4127 ? addNewField('inspectors') : addField('inspectors')}
        />
      }
      tooltip={<KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/inspectors`} />}
    >
      <FieldArray name={`${name}.inspectors`}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormInspector, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((fieldName, index) => {
                const fieldValue: IKnmErknmFormInspector | undefined = fields.value?.[index];
                const inspectorId = getResultDecisionInspectorBlockId(id, fieldValue?.listId || index.toString());

                return (
                  <div key={inspectorId} style={{ width: '100%', marginBottom: '8px' }}>
                    <Row margin="medium" isListItem notSpace notWrap>
                      {isShow4127 && inspectorsSelectorOrInput[index] ? (
                        <>
                          <SelectInputField
                            className={styles.SelectInputKnoInspectors}
                            name={`${fieldName}`}
                            id={`${inspectorId}`}
                            placeholder="Выберите должностное лицо"
                            options={inspectorsActsOptions}
                            isSearchable
                            disabled={watchOnly}
                            size="medium"
                            onChange={option => option && fields.update(index, { ...option, ...option.value })}
                          />
                          <KnmLateFieldsRemark
                            lateFieldPath={`${lateFieldName}/inspectors/${fieldValue?.listId}`}
                            fieldId={`${inspectorId}`}
                          />
                        </>
                      ) : (
                        <>
                          <FieldBlock size="half" id={`${inspectorId}.fullName`}>
                            <TextareaField
                              name={`${fieldName}.fullName`}
                              placeholder="Введите ФИО"
                              disabled={watchOnly}
                            />
                            <KnmLateFieldsRemark
                              lateFieldPath={`${lateFieldName}/inspectors/${fieldValue?.listId}/fullName`}
                              fieldId={`${inspectorId}.fullName`}
                            />
                          </FieldBlock>
                          <SelectInputField
                            className={styles.InspectorsSelect}
                            name={`${fieldName}.position`}
                            id={`${inspectorId}.position`}
                            placeholder="Выберите должность"
                            options={participantPositionsSelectOptions}
                            size="medium"
                            disabled={watchOnly}
                            isClearable={false}
                          />
                          <KnmLateFieldsRemark
                            lateFieldPath={`${lateFieldName}/inspectors/${fieldValue?.listId}/position/title`}
                            fieldId={`${inspectorId}.position`}
                          />
                        </>
                      )}
                      <EditButton
                        className="shared-deleting-row-btn"
                        onClick={() => !watchOnly && onChangeInspectorTypeInput(fields, index)}
                      />
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveField('inspectors', index)} />
                    </Row>
                    <FormSpy subscription={{ errors: true, touched: true }}>
                      {props => {
                        const fieldError = R.path(
                          ['resultDecisions', decisionIndex, 'inspectors', index],
                          props.errors,
                        ) as Object | undefined;

                        const error =
                          inspectorsSelectorOrInput[index] && fieldError
                            ? Object.values(fieldError).join('')
                            : undefined;

                        const isFieldTouched = R.path([`${name}.inspectors[${index}]`], props.touched) as boolean;

                        if (error && isFieldTouched) {
                          return (
                            <p style={{ marginTop: '-22px' }} className="shared-input-error">
                              {error}
                            </p>
                          );
                        }

                        return null;
                      }}
                    </FormSpy>
                  </div>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionInspectorsStoreProps => ({
  participantPositionsSelectOptions: getKnmErknmParticipantPositionsSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
});

export const ErknmResultDecisionInspectors = connect(mapStateToProps)(ErknmResultDecisionInspectorsWithoutStore);
